'use client';

/**
 * DesignSystemProvider
 *
 * Root provider that wraps the application with theme and animation context.
 * Supports portal-specific color theming (Admin=Gold, Business=Blue, Consumer=Green).
 */

import CssBaseline from '@mui/material/CssBaseline';
import type { Theme } from '@mui/material/styles';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AnimatePresence } from 'framer-motion';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { I18nProvider } from '../hooks/useI18n';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { type UseThemeModeOptions, useThemeMode } from '../hooks/useThemeMode';
import {
  availableDesignGuidelines,
  availableThemeModes,
  cycleThemeMode,
  type DesignGuideline,
  designGuidelineLabels,
  getInitialDesignGuideline,
  getPortalPalette,
  getTheme,
  type PortalColorPalette,
  type PortalType,
  storeDesignGuideline,
  type ThemeMode,
  themeModeLabels,
} from '../theme';

// ============================================================================
// Types
// ============================================================================

export interface DesignSystemContextValue {
  /**
   * Current theme mode
   */
  mode: ThemeMode;

  /**
   * Set theme mode
   */
  setMode: (mode: ThemeMode | 'system') => void;

  /**
   * Toggle between light and dark (legacy)
   */
  toggleMode: () => void;

  /**
   * Cycle through all available themes: light -> dark -> twilight -> midnight -> light
   */
  cycleMode: () => void;

  /**
   * All available theme modes
   */
  availableModes: ThemeMode[];

  /**
   * Get display label for a theme mode
   */
  getModeLabel: (mode: ThemeMode) => string;

  /**
   * Whether following system preference
   */
  isSystemMode: boolean;

  /**
   * System's preferred color scheme
   */
  systemPreference: ThemeMode;

  /**
   * Whether user prefers reduced motion
   */
  prefersReducedMotion: boolean;

  /**
   * Current design guideline ('apple' for Liquid Glass, 'material' for MD3)
   */
  designGuideline: DesignGuideline;

  /**
   * Set design guideline
   */
  setDesignGuideline: (guideline: DesignGuideline) => void;

  /**
   * All available design guidelines
   */
  availableGuidelines: DesignGuideline[];

  /**
   * Get display label for a design guideline
   */
  getGuidelineLabel: (guideline: DesignGuideline) => string;
}

export interface DesignSystemProviderProps {
  /**
   * Children components
   */
  children: React.ReactNode;

  /**
   * Initial theme mode
   * @default 'system'
   */
  defaultMode?: ThemeMode | 'system';

  /**
   * Storage key for persisting theme
   * @default 'theme-mode'
   */
  storageKey?: string;

  /**
   * Callback when theme changes
   */
  onThemeChange?: (mode: ThemeMode) => void;

  /**
   * Whether to include CssBaseline
   * @default true
   */
  includeCssBaseline?: boolean;

  /**
   * Enable page transition animations
   * @default true
   */
  enableAnimations?: boolean;

  /**
   * Portal type for portal-specific colors
   * - 'admin': Gold theme
   * - 'business': Blue (Outlook-inspired) theme
   * - 'consumer': Green (Excel-inspired) theme
   * @default undefined (uses design system default purple)
   */
  portalType?: PortalType;

  /**
   * Design guideline to use
   * - 'apple': Apple Liquid Glass (glassmorphism, pill buttons, blur)
   * - 'material': Google Material Design 3 (tonal surfaces, rounded rects, solid)
   * @default 'apple'
   */
  designGuideline?: DesignGuideline;
}

// ============================================================================
// Context
// ============================================================================

const DesignSystemContext = createContext<DesignSystemContextValue | undefined>(undefined);

// ============================================================================
// Hook
// ============================================================================

/**
 * Access design system context
 */
export function useDesignSystem(): DesignSystemContextValue {
  const context = useContext(DesignSystemContext);
  if (!context) {
    throw new Error('useDesignSystem must be used within a DesignSystemProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

const PORTAL_TEMPLATE_BY_TYPE: Record<PortalType, 'gold' | 'blue' | 'green'> = {
  admin: 'gold',
  business: 'blue',
  consumer: 'green',
};

/**
 * Apply portal-specific colors to a base theme
 */
function applyPortalColors(baseTheme: Theme, portalPalette: PortalColorPalette, isDark: boolean): Theme {
  const colors = isDark ? portalPalette.primaryDark : portalPalette.primary;

  return createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      primary: {
        main: colors[500],
        light: colors[400],
        dark: colors[700],
        contrastText: isDark ? '#000000' : '#FFFFFF',
      },
      secondary: {
        main: portalPalette.accent,
        light: portalPalette.accentLight,
        dark: colors[800],
        contrastText: '#FFFFFF',
      },
    },
  });
}

export const DesignSystemProvider: React.FC<DesignSystemProviderProps> = ({
  children,
  defaultMode = 'system',
  storageKey = 'theme-mode',
  onThemeChange,
  includeCssBaseline = true,
  enableAnimations = true,
  portalType,
  designGuideline: guidelineProp,
}) => {
  // Theme mode management
  const themeModeOptions: UseThemeModeOptions = useMemo(
    () => ({
      storageKey,
      defaultMode,
      onThemeChange,
    }),
    [storageKey, defaultMode, onThemeChange],
  );

  const { mode, setMode, toggle, isSystemMode, systemPreference } = useThemeMode(themeModeOptions);

  // Design guideline management
  const [guideline, setGuidelineState] = useState<DesignGuideline>(() => guidelineProp ?? getInitialDesignGuideline());

  const setDesignGuideline = useCallback((newGuideline: DesignGuideline) => {
    setGuidelineState(newGuideline);
    storeDesignGuideline(newGuideline);
  }, []);

  const getGuidelineLabel = useCallback((g: DesignGuideline) => designGuidelineLabels[g] || g, []);

  // Accessibility
  const prefersReducedMotion = useReducedMotion();

  // Get current theme with guideline and optional portal-specific colors
  const theme = useMemo(() => {
    const baseTheme = getTheme(mode, guideline);
    const isDark = mode !== 'light';

    // If no portal type specified, use base theme
    if (!portalType) {
      return baseTheme;
    }

    // Apply portal-specific colors
    const portalPalette = getPortalPalette(PORTAL_TEMPLATE_BY_TYPE[portalType]);
    return applyPortalColors(baseTheme, portalPalette, isDark);
  }, [mode, portalType, guideline]);

  // Cycle through all themes
  const cycleMode = useCallback(() => {
    const nextMode = cycleThemeMode(mode);
    setMode(nextMode);
  }, [mode, setMode]);

  // Get label for a mode
  const getModeLabel = useCallback((themeMode: ThemeMode) => themeModeLabels[themeMode] || themeMode, []);

  // Context value
  const contextValue = useMemo<DesignSystemContextValue>(
    () => ({
      mode,
      setMode,
      toggleMode: toggle,
      cycleMode,
      availableModes: availableThemeModes,
      getModeLabel,
      isSystemMode,
      systemPreference,
      prefersReducedMotion,
      designGuideline: guideline,
      setDesignGuideline,
      availableGuidelines: availableDesignGuidelines,
      getGuidelineLabel,
    }),
    [
      mode,
      setMode,
      toggle,
      cycleMode,
      getModeLabel,
      isSystemMode,
      systemPreference,
      prefersReducedMotion,
      guideline,
      setDesignGuideline,
      getGuidelineLabel,
    ],
  );

  return (
    <DesignSystemContext.Provider value={contextValue}>
      <I18nProvider>
        <MuiThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {includeCssBaseline && <CssBaseline />}
            {enableAnimations ? <AnimatePresence mode="wait">{children}</AnimatePresence> : children}
          </LocalizationProvider>
        </MuiThemeProvider>
      </I18nProvider>
    </DesignSystemContext.Provider>
  );
};

// ============================================================================
// Theme Toggle Component
// ============================================================================

export interface ThemeToggleProps {
  /**
   * Custom render function for toggle button
   */
  children?: (props: {
    mode: ThemeMode;
    toggle: () => void;
    cycle: () => void;
    setMode: (mode: ThemeMode | 'system') => void;
    availableModes: ThemeMode[];
    getModeLabel: (mode: ThemeMode) => string;
    isSystemMode: boolean;
  }) => React.ReactNode;
}

/**
 * Theme mode icons
 */
const themeModeIcons: Record<ThemeMode, string> = {
  light: '☀️',
  dark: '🌙',
  twilight: '🌅',
  midnight: '🌑',
};

/**
 * Theme toggle component with multi-theme support
 *
 * By default, cycles through all 4 themes on click.
 * Use the render prop pattern for custom UI.
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ children }) => {
  const { mode, toggleMode, cycleMode, setMode, availableModes, getModeLabel, isSystemMode } = useDesignSystem();

  if (children) {
    return (
      <>
        {children({
          mode,
          toggle: toggleMode,
          cycle: cycleMode,
          setMode,
          availableModes,
          getModeLabel,
          isSystemMode,
        })}
      </>
    );
  }

  // Default toggle button - cycles through all themes
  return (
    <button
      onClick={cycleMode}
      aria-label={`Current theme: ${getModeLabel(mode)}. Click to cycle themes.`}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '1.5rem',
        padding: 8,
      }}
    >
      {themeModeIcons[mode]}
    </button>
  );
};

export default DesignSystemProvider;
